/* eslint-disable @typescript-eslint/no-floating-promises */

'use client'

import React, { useEffect, useState } from 'react'
import { fetchDeepLink } from '@services/fetchDeepLink'
import { WidgetType } from '@enums/WidgetType'
import { commonWidgetConfig } from '@components/MainWidget/common/commonWidgetConfig'
import { TAP_BTN_EVENT } from '@components/MainWidget/FastagWidget/constants'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import {
  fetchWidgetProductFn,
  widgetJourney,
} from '@configs/segmentEvents.config'
import { TAP_BTN_ENGAGE } from '@constants/homeSegmentEvent'
import { SEGMENT } from '@components/MainWidget/common/widgetSegments'
import styles from './MobileChallanPPED.module.scss'
import AllChallansBottomSheet, { type ChallanListItem } from './AllChallansBottomSheet'
import ChallanCard from './challanCard'

const VERIFIED_BADGE_SRC = 'https://acko-cms.ackoassets.com/SUV_Light_ce285abfd0.webp'

export type { ChallanListItem }

export type MobileChallanModalPPEDProps = {
  regNum: string
  challanList?: ChallanListItem[]
  expiryDate?: string
  onSubmit?: () => void
  onViewAllChallans?: () => void
  vehicleInfoData?: any
  widgetType?: string,
  segmentEventCommonProperties?: any
}

const NoChallanFound = () => (
  <div className={styles.noChallanFound}>
    <div className={styles.successIconWrap}>
      <img src="https://acko-cms.ackoassets.com/Success_cf25f88430.svg" />
    </div>
    <div className={styles.noChallanTitle}>No challans found</div>
  </div>
)

const ChallanListSection = ({
  challanList,
  onViewAllChallans,
}: {
  challanList: ChallanListItem[]
  onViewAllChallans: () => void
}) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  return (
    <div className={styles.challanList}>
      {challanList.slice(0, 2).map((challan, index) => (
        <ChallanCard
          key={`${challan.title}-${index}`}
          {...challan}
          isExpanded={expandedIndex === index}
          onToggleExpand={() => setExpandedIndex(prev => (prev === index ? null : index))}
        />
      ))}
      {challanList.length > 0 && (
        <div
          role="button"
          tabIndex={0}
          className={styles.viewAllChallans}
          onClick={onViewAllChallans}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') onViewAllChallans()
          }}
        >
          <div className={styles.viewAllChallansText}>View all challans →</div>
        </div>
      )}
    </div>
  )
}

const MobileChallanModalPPED = ({
  regNum,
  challanList = [],
  expiryDate,
  onViewAllChallans,
  vehicleInfoData,
  widgetType = WidgetType.CHALLAN_WIDGET_LANDING,
  segmentEventCommonProperties
}: MobileChallanModalPPEDProps) => {
  const [showAllChallansSheet, setShowAllChallansSheet] = useState(false)
  const [downloadUrl, setDownloadUrl] = useState('')
  const hasChallans = challanList?.length > 0

  useEffect(() => {
    const fetchData = async () => {
      const deepLink = await fetchDeepLink({
        regNo: vehicleInfoData?.reg_number || regNum,
        widgetType,
        mobileNo: vehicleInfoData?.mobile_number || '',
        selectedProvider: vehicleInfoData?.selectedProvider,
      })
      setDownloadUrl(deepLink || commonWidgetConfig.default_deep_link[widgetType] || '')
    }

    void fetchData()
  }, [])

  const handleViewAllChallans = () => {
    trackEvent(TAP_BTN_ENGAGE, {
      ...segmentEventCommonProperties,
      journey: widgetJourney?.[widgetType],
      product: fetchWidgetProductFn?.[widgetType]?.(),
      phone: vehicleInfoData?.mobile_number || '',
      registration_number: vehicleInfoData?.reg_number || '',
      cta_text: 'view_all_challans',
    })
    setShowAllChallansSheet(true)
    onViewAllChallans?.()
  }

  const handleCheckPrice = () => {
    const redirect = () => {
      window.location.href = `/t/car?carNumber=${regNum}`
    }
    trackEvent(TAP_BTN_EVENT, {
      ...segmentEventCommonProperties,
      journey: widgetJourney?.[widgetType],
      product: fetchWidgetProductFn?.[widgetType]?.(),
      phone: vehicleInfoData?.mobile_number || '',
      registration_number: vehicleInfoData?.reg_number || '',
      cta_text: 'check price',
      action_type: 'redirection',
      redirection_url: '/t/car',
    })
      .then(redirect)
      .catch(redirect)
  }

  const handlePayChallans = (amount) => {
    window.location.href = downloadUrl
    trackEvent(SEGMENT.TAB_BTN_ACKO, {
      ...segmentEventCommonProperties,
      journey: widgetJourney?.[widgetType],
      product: fetchWidgetProductFn?.[widgetType]?.(),
      phone: vehicleInfoData?.mobile_number || '',
      registration_number: vehicleInfoData?.reg_number || '',
      cta_text: 'Pay challans',
      state_name: amount,
      variant_name: 'with_insurance_reminder'
    })
  }

  return (
    <div className={styles.mobileChallanModalPPED}>
      <div className={styles.ppedHeader}>
        <div className={styles.ppedHeaderLeft}>
          <div className={styles.ppedTitle}>Your challan details</div>
          <div className={styles.ppedVehicleName}>{vehicleInfoData?.vehicleInfo?.db_make_name} {vehicleInfoData?.vehicleInfo?.db_model_name}</div>
          <div className={styles.numberPlate}>
            <div className={styles.numberPlateBadge}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0.5" y="0.5" width="9" height="9" rx="4.5" stroke="white" strokeLinejoin="round" strokeDasharray="1 2" />
                <rect x="3" y="3" width="4" height="4" rx="1" fill="url(#paint0_linear_13057_25269)" />
                <rect width="2" height="2" transform="translate(4 4)" fill="#E7E7F0" />
                <defs>
                  <linearGradient id="paint0_linear_13057_25269" x1="3.5" y1="3" x2="6.5" y2="7" gradientUnits="userSpaceOnUse">
                    <stop stopColor="white" />
                    <stop offset="0.0001" stopColor="white" />
                    <stop offset="0.505" stopColor="#FFF8F8" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                </defs>
              </svg>
              <div className={styles.numberPlateInd}>IND</div>
            </div>
            <div className={styles.numberPlateValue}>{regNum}</div>
          </div>
        </div>
        <img height={80} alt="verified" src={vehicleInfoData?.image || VERIFIED_BADGE_SRC} className={styles.verifiedBadge} />
      </div>

      {hasChallans ? (
        <ChallanListSection challanList={challanList} onViewAllChallans={handleViewAllChallans} />
      ) : (
        <NoChallanFound />
      )}

      <div className={styles.ppedBottom}>
        <div className={styles.ppedBanner}>
          <div className={styles.ppedBannerText}>
            {hasChallans ? (
              <>
                Got challans?
                <br />
                You can still save on car insurance
              </>
            ) : (
              'Zero challans = Lower price on ACKO'
            )}
          </div>
        </div>

        <div className={styles.ppedCard}>
          <div className={styles.ppedCardContent}>
            <div className={styles.warningIconWrap}>
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M28 49C39.598 49 49 39.598 49 28C49 16.402 39.598 7 28 7C16.402 7 7 16.402 7 28C7 39.598 16.402 49 28 49Z" fill="url(#paint0_linear_13057_25291)" />
                <path opacity="0.8" d="M27.9999 43.3996C36.505 43.3996 43.3999 36.5048 43.3999 27.9996C43.3999 19.4944 36.505 12.5996 27.9999 12.5996C19.4947 12.5996 12.5999 19.4944 12.5999 27.9996C12.5999 36.5048 19.4947 43.3996 27.9999 43.3996Z" fill="white" />
                <path d="M27.9999 36.3992C26.8405 36.3992 25.8999 35.4598 25.8999 34.2992C25.8999 33.1386 26.8405 32.1992 27.9999 32.1992C29.1592 32.1992 30.0999 33.1386 30.0999 34.2992C30.0999 35.4598 29.1592 36.3992 27.9999 36.3992Z" fill="#D83D37" />
                <path d="M27.9998 30.0996C26.8405 30.0996 25.8998 29.1602 25.8998 27.9996C25.8998 27.2463 25.8998 23.9317 25.8998 21.6983C25.8998 20.5385 26.84 19.5996 27.9998 19.5996C29.1596 19.5996 30.0999 20.5386 30.0999 21.6984C30.0998 24.0589 30.0998 27.6155 30.0998 27.9996C30.0998 29.1602 29.1592 30.0996 27.9998 30.0996Z" fill="#D83D37" />
                <path opacity="0.1" d="M27.8551 54.5988C18.6583 54.5988 11.2 53.9558 11.2 53.1588C11.2 52.3617 18.6583 51.7188 27.8551 51.7188C37.052 51.7188 44.5103 52.3617 44.5103 53.1588C44.5103 53.9558 37.052 54.5988 27.8551 54.5988Z" fill="url(#paint1_radial_13057_25291)" />
                <defs>
                  <linearGradient id="paint0_linear_13057_25291" x1="33.8785" y1="66.8671" x2="33.8785" y2="-7.54516" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#7D7D7F" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                  <radialGradient id="paint1_radial_13057_25291" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(29.0365 52.9119) rotate(180) scale(16.4425 1.55383)">
                    <stop />
                    <stop offset="1" stopColor="#414042" stopOpacity="0" />
                  </radialGradient>
                </defs>
              </svg>
            </div>

            <div className={styles.ppedCardText}>
              {expiryDate && <div className={styles.expiryText}>Your insurance expires on {expiryDate}</div>}
              <div className={styles.ppedCardHeadline}>
                Check your ACKO insurance
                <br />
                price and save big!
              </div>
            </div>
          </div>

          <button type="button" className={styles.ppedCta} onClick={handleCheckPrice}>
            Check your price
          </button>
        </div>
      </div>

      <AllChallansBottomSheet
        isOpen={showAllChallansSheet}
        challans={challanList}
        onClose={() => setShowAllChallansSheet(false)}
        onPayPress={handlePayChallans}
      />
    </div>
  )
}

export default MobileChallanModalPPED
